import React from 'react';
import { Shield, Calendar, Headphones, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const WhyBookDirect = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: Shield,
      titleKey: 'bestPrice',
      descKey: 'bestPriceDesc',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: Calendar,
      titleKey: 'freeCancellation',
      descKey: 'freeCancellationDesc',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Headphones,
      titleKey: 'directSupport',
      descKey: 'directSupportDesc',
      color: 'from-amber-600 to-amber-700'
    }
  ];

  return (
    <section className="py-12 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 px-4 py-2 rounded-full mb-4">
            <CheckCircle className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">
              {t('whyBookDirect.exclusive')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('whyBookDirect.title')}
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            {t('whyBookDirect.subtitle')}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div 
              key={benefit.titleKey}
              className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-amber-500/50"
              data-testid={`benefit-card-${index}`}
            >
              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${benefit.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                <benefit.icon className="w-7 h-7 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-white mb-2">
                {t(`whyBookDirect.${benefit.titleKey}`)}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {t(`whyBookDirect.${benefit.descKey}`)}
              </p>

              {/* Hover Glow Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`} />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <a 
            href="#rooms"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-amber-500/25 hover:scale-105"
            data-testid="book-direct-cta"
          >
            {t('whyBookDirect.bookNow')}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhyBookDirect;
